/** @forge-header
 * @id          sportsStream
 * @role        Multi-sport streaming orchestrator | WS + REST + discovery + Poly hydration
 * @pattern     orchestrator
 * @purity      ws,api
 * @units       cents
 * @money-path  no
 * @fan-out     ~3
 * @state       gamesByEvent:Map | marketBooks:Map | discoveredMarkets:arr | loadingState:obj | polyTokenToEvent:Map | scaffoldGroupedEvents:Map
 * @lifecycle   init -> discover -> fetch-books(parallel) -> build-games -> ws-subscribe -> poly-hydrate(bg) -> live
 * @upstream    sportsDiscovery:discoverSportsMarkets,groupMarketsByEvent | marketStream:createMarketStream | sportsStream/gameBuilder:* | sportsStream/polyHydration:*
 * @downstream  useSportsStream
 * @exports     createSportsStream:fn | SportsStream:interface | StreamConnectionOptions:interface
 * @test        stream.test.ts (8 cases)
 *
 * @invariant   polyHydrationCancelled gates all async hydration | prevents race on stop()
 * @invariant   MAX_PER_EVENT=6 filters exotic lines | changing affects orderbook fetch volume
 * @danger      stop() must clear ALL Maps + arrays | missing one = memory leak on restart
 */

import type { KalshiApiClient } from '../../types';
import type { Environment } from '@galactus/shared';
import { createMarketStream, type MarketStream } from '../market/marketStream';
import {
  discoverSportsMarkets,
  groupMarketsByEvent,
  type DiscoveredMarket,
  type GroupedEvent,
} from '../sportsDiscovery';
import { applyStartTimeOffset } from '../nbaConsolidated/startTimeSettings';
import { setEventStartTimeUtc, clearEventStartTimes } from '../market/eventStartTimeStore';
import type {
  SportsStreamOptions,
  SportsStreamUpdate,
  SportsLoadingState,
  SportsLoadPhase,
  SportsLoadEvent,
  GameData,
  MoneylineGameData,
  SpreadGameData,
  TotalGameData,
  MarketBook,
} from './types';
import {
  mapConcurrent,
  parseTeamNamesFromTitle,
  orderbookToLevels,
  buildMoneylineGame,
  buildSpreadGame,
  buildTotalGame,
} from './gameBuilder';
import {
  hydratePolyMoneylines,
  hydratePolySpreads,
  hydratePolyTotals,
  type PolyTokenMapping,
  type PolyHydrationDeps,
} from './polyHydration';

/** Stream connection options */
export interface StreamConnectionOptions {
  api: KalshiApiClient | null;
  accessKeyId: string;
  privateKey: CryptoKey;
  environment: Environment;
  useRelay: boolean;
  useMock: boolean;
}

/** Callback for stream updates */
type StreamCallback = (update: SportsStreamUpdate) => void;

export interface SportsStream {
  start: () => Promise<void>;
  stop: () => void;
  onUpdate: (callback: StreamCallback) => void;
  offUpdate: (callback: StreamCallback) => void;
}

/**
 * Create a sports stream for a specific sport and market type
 */
export function createSportsStream(
  connectionOptions: StreamConnectionOptions,
  streamOptions: SportsStreamOptions
): SportsStream {
  const { api, accessKeyId, privateKey, environment, useRelay, useMock } = connectionOptions;
  const { sport, marketType, daysAhead = 7 } = streamOptions;

  const callbacks: Set<StreamCallback> = new Set();
  let marketStream: MarketStream | null = null;
  let polyStream: ReturnType<
    typeof import('../polymarket/marketStream').createPolyMarketStream
  > | null = null;
  let polyHydrationCancelled = false;

  // Internal state
  const gamesByEvent = new Map<string, GameData>();
  const marketBooks = new Map<string, MarketBook>();
  const eventStartTimes = new Map<string, string | null>(); // eventTicker -> startTimePt
  const eventTeamNames = new Map<string, { awayName: string; homeName: string }>(); // eventTicker -> names from title
  let discoveredMarkets: DiscoveredMarket[] = [];
  let fallbackEventScaffoldOnly = false;
  // Events found via moneyline cross-reference that lack spread/total markets (BUG-009)
  const scaffoldGroupedEvents = new Map<string, GroupedEvent>();
  let tickers: string[] = [];
  let lastUpdateMs = 0;
  const polyTokenToEvent = new Map<string, PolyTokenMapping>();
  const polyMoneylineByEvent = new Map<string, NonNullable<MoneylineGameData['polymarket']>>();
  const polySpreadByEvent = new Map<string, NonNullable<SpreadGameData['polymarketMain']>>();
  const polyTotalByEvent = new Map<string, NonNullable<TotalGameData['polymarketMain']>>();
  let loadingState: SportsLoadingState = {
    isLoading: false,
    phase: 'idle',
    startedAtMs: 0,
    updatedAtMs: 0,
    events: [],
  };

  const updateLoadingState = (
    phase: SportsLoadPhase,
    args?: { message?: string; done?: number; total?: number; level?: SportsLoadEvent['level'] }
  ) => {
    const now = Date.now();
    const shouldAppendEvent = typeof args?.message === 'string' && args.message.length > 0;
    loadingState = {
      ...loadingState,
      phase,
      isLoading: phase !== 'live' && phase !== 'idle' && phase !== 'error',
      updatedAtMs: now,
      done: args?.done,
      total: args?.total,
      events: shouldAppendEvent
        ? [
            ...loadingState.events,
            {
              tsMs: now,
              phase,
              level: args?.level ?? 'info',
              message: args.message ?? '',
              done: args?.done,
              total: args?.total,
            },
          ]
        : loadingState.events,
    };
  };

  const emit = () => {
    const games = Array.from(gamesByEvent.values());
    // Sort by date, then by team codes
    games.sort((a, b) => {
      const dateComp = (a.date || '').localeCompare(b.date || '');
      if (dateComp !== 0) return dateComp;
      const awayComp = a.awayCode.localeCompare(b.awayCode);
      if (awayComp !== 0) return awayComp;
      return a.homeCode.localeCompare(b.homeCode);
    });

    const payload: SportsStreamUpdate = {
      sport,
      marketType,
      games,
      lastUpdateMs,
      loadingState: {
        ...loadingState,
        events: [...loadingState.events],
      },
    };

    callbacks.forEach((cb) => {
      try {
        cb(payload);
      } catch (err) {
        console.error('Error in sports stream callback:', err);
      }
    });
  };

  const rebuildGames = () => {
    const grouped = groupMarketsByEvent(discoveredMarkets);

    gamesByEvent.clear();
    for (const [eventTicker, event] of grouped.entries()) {
      const titleNames = eventTeamNames.get(eventTicker);
      const eventForBuild: GroupedEvent = titleNames
        ? {
            ...event,
            awayName: titleNames.awayName,
            homeName: titleNames.homeName,
          }
        : event;

      // Build market book map for this event
      const eventBooks = new Map<string, MarketBook>();
      for (const [outcomeKey, dm] of eventForBuild.markets.entries()) {
        const book = marketBooks.get(dm.marketTicker);
        if (book) {
          if (marketType === 'moneyline') {
            // Moneyline lookup can happen by parsed outcome code (away/home) or raw market ticker.
            eventBooks.set(outcomeKey, book);
            eventBooks.set(dm.marketTicker, book);
          } else {
            eventBooks.set(dm.marketTicker, book);
          }
        }
      }

      // Build appropriate game data based on market type
      let gameData: GameData;
      if (marketType === 'moneyline') {
        gameData = buildMoneylineGame(eventForBuild, eventBooks);
        const poly = polyMoneylineByEvent.get(eventTicker);
        if (poly) {
          gameData.polymarket = poly;
        }
      } else if (marketType === 'spread') {
        gameData = fallbackEventScaffoldOnly
          ? {
              marketType: 'spread',
              eventTicker: eventForBuild.eventTicker,
              date: eventForBuild.dateYyyyMmDd,
              awayCode: eventForBuild.awayCode,
              homeCode: eventForBuild.homeCode,
              awayName: eventForBuild.awayName,
              homeName: eventForBuild.homeName,
              startTimePt: null,
              markets: new Map(),
              polymarketMain: null,
            }
          : buildSpreadGame(eventForBuild, eventBooks);
        const polyMain = polySpreadByEvent.get(eventTicker);
        if (polyMain) {
          gameData.polymarketMain = polyMain;
        }
      } else {
        gameData = fallbackEventScaffoldOnly
          ? {
              marketType: 'total',
              eventTicker: eventForBuild.eventTicker,
              date: eventForBuild.dateYyyyMmDd,
              awayCode: eventForBuild.awayCode,
              homeCode: eventForBuild.homeCode,
              awayName: eventForBuild.awayName,
              homeName: eventForBuild.homeName,
              startTimePt: null,
              markets: new Map(),
              polymarketMain: null,
            }
          : buildTotalGame(eventForBuild, eventBooks);
        const polyMain = polyTotalByEvent.get(eventTicker);
        if (polyMain) {
          gameData.polymarketMain = polyMain;
        }
      }

      // Apply stored start time if available
      const storedStartTime = eventStartTimes.get(eventTicker);
      if (storedStartTime !== undefined) {
        gameData.startTimePt = storedStartTime;
      }

      // Apply team names from event title (overrides discovery-based names)
      if (titleNames) {
        gameData.awayName = titleNames.awayName;
        gameData.homeName = titleNames.homeName;
      }

      gamesByEvent.set(eventTicker, gameData);
    }

    // BUG-009: Add scaffold games for events found via moneyline cross-reference
    for (const [eventTicker, mlEvent] of scaffoldGroupedEvents) {
      const titleNames = eventTeamNames.get(eventTicker);
      const gameData: GameData =
        marketType === 'spread'
          ? {
              marketType: 'spread',
              eventTicker,
              date: mlEvent.dateYyyyMmDd,
              awayCode: mlEvent.awayCode,
              homeCode: mlEvent.homeCode,
              awayName: titleNames?.awayName ?? mlEvent.awayName,
              homeName: titleNames?.homeName ?? mlEvent.homeName,
              startTimePt: null,
              markets: new Map(),
              polymarketMain: null,
            }
          : {
              marketType: 'total',
              eventTicker,
              date: mlEvent.dateYyyyMmDd,
              awayCode: mlEvent.awayCode,
              homeCode: mlEvent.homeCode,
              awayName: titleNames?.awayName ?? mlEvent.awayName,
              homeName: titleNames?.homeName ?? mlEvent.homeName,
              startTimePt: null,
              markets: new Map(),
              polymarketMain: null,
            };

      const storedStartTime = eventStartTimes.get(eventTicker);
      if (storedStartTime !== undefined) {
        gameData.startTimePt = storedStartTime;
      }

      gamesByEvent.set(eventTicker, gameData);
    }
  };

  const initialize = async () => {
    gamesByEvent.clear();
    marketBooks.clear();
    polyMoneylineByEvent.clear();
    polySpreadByEvent.clear();
    polyTotalByEvent.clear();
    eventStartTimes.clear();
    eventTeamNames.clear();
    tickers = [];
    lastUpdateMs = Date.now();
    loadingState = {
      isLoading: true,
      phase: 'discovering-markets',
      startedAtMs: lastUpdateMs,
      updatedAtMs: lastUpdateMs,
      events: [],
    };
    updateLoadingState('discovering-markets', {
      message: `Discovering ${sport.toUpperCase()} ${marketType} markets`,
    });
    emit();

    if (useMock || !api) {
      updateLoadingState('live', { message: 'Using mock/no API mode' });
      emit();
      return;
    }

    // Discover markets for this sport/market type
    discoveredMarkets = await discoverSportsMarkets(api, {
      sport,
      marketType,
      daysAhead,
      limit: 200,
    });

    fallbackEventScaffoldOnly = false;
    scaffoldGroupedEvents.clear();
    if (
      discoveredMarkets.length === 0 &&
      (sport === 'tennis-atp' || sport === 'tennis-wta') &&
      (marketType === 'spread' || marketType === 'total')
    ) {
      discoveredMarkets = await discoverSportsMarkets(api, {
        sport,
        marketType: 'moneyline',
        daysAhead,
        limit: 200,
      });
      fallbackEventScaffoldOnly = discoveredMarkets.length > 0;
    }

    // BUG-009: Cross-reference moneyline to find games missing from spread/total
    // Kalshi may not list spread/total markets for less popular games (e.g., early CBB).
    // Discover moneyline (broadest coverage) and scaffold missing games.
    if (
      !fallbackEventScaffoldOnly &&
      discoveredMarkets.length > 0 &&
      (marketType === 'spread' || marketType === 'total')
    ) {
      try {
        const moneylineMarkets = await discoverSportsMarkets(api, {
          sport,
          marketType: 'moneyline',
          daysAhead,
          limit: 200,
        });

        if (moneylineMarkets.length > 0) {
          // Build game keys from spread/total discovery
          const mainGrouped = groupMarketsByEvent(discoveredMarkets);
          const mainGameKeys = new Set<string>();
          for (const ev of mainGrouped.values()) {
            mainGameKeys.add(`${ev.dateYyyyMmDd}-${ev.awayCode}-${ev.homeCode}`);
          }

          // Find moneyline games missing from spread/total
          const mlGrouped = groupMarketsByEvent(moneylineMarkets);
          for (const [mlEventTicker, mlEvent] of mlGrouped) {
            const gameKey = `${mlEvent.dateYyyyMmDd}-${mlEvent.awayCode}-${mlEvent.homeCode}`;
            if (!mainGameKeys.has(gameKey)) {
              scaffoldGroupedEvents.set(mlEventTicker, mlEvent);
            }
          }

          if (scaffoldGroupedEvents.size > 0) {
            updateLoadingState('discovering-markets', {
              message: `Found ${scaffoldGroupedEvents.size} games without ${marketType} markets`,
            });
            emit();
          }
        }
      } catch (err) {
        console.warn('Moneyline cross-reference failed (non-fatal):', err);
      }
    }

    if (discoveredMarkets.length === 0) {
      console.warn(`No ${sport} ${marketType} markets found`);
      updateLoadingState('live', {
        message: `No ${sport.toUpperCase()} ${marketType} markets found`,
        level: 'warn',
      });
      emit();
      return;
    }

    // Collect unique tickers
    tickers = [...new Set(discoveredMarkets.map((m) => m.marketTicker))];

    // For spread/total: only fetch orderbooks for the nearest-center buckets per event.
    // Exotic far-out lines (e.g., "wins by 20+") rarely have liquidity and just slow things down.
    if (marketType === 'spread' || marketType === 'total') {
      const MAX_PER_EVENT = 6;
      const grouped = groupMarketsByEvent(discoveredMarkets);
      const keepTickers = new Set<string>();

      for (const [, event] of grouped) {
        const marketsArr = Array.from(event.markets.values());
        if (marketsArr.length <= MAX_PER_EVENT) {
          for (const dm of marketsArr) keepTickers.add(dm.marketTicker);
          continue;
        }

        // Find median floorStrike, keep the closest N buckets to it
        const strikes = marketsArr.map((dm) => dm.floorStrike ?? 0).sort((a, b) => a - b);
        const median = strikes[Math.floor(strikes.length / 2)] ?? 0;

        const sorted = [...marketsArr].sort((a, b) => {
          return Math.abs((a.floorStrike ?? 0) - median) - Math.abs((b.floorStrike ?? 0) - median);
        });

        for (let i = 0; i < MAX_PER_EVENT; i++) {
          keepTickers.add(sorted[i].marketTicker);
        }
      }

      const beforeCount = tickers.length;
      tickers = tickers.filter((t) => keepTickers.has(t));
      if (tickers.length < beforeCount) {
        updateLoadingState('discovering-markets', {
          message: `Filtered ${beforeCount} → ${tickers.length} tickers (main lines only)`,
        });
        emit();
      }
    }

    // Start WS connect early (in parallel with REST fetches below).
    // connect() only needs auth credentials, not tickers. subscribeOrderbook() is separate.
    marketStream = createMarketStream(useRelay);

    marketStream.onError((error) => {
      console.error(`Sports stream error (${sport} ${marketType}):`, error);
      updateLoadingState(loadingState.phase === 'live' ? 'live' : 'error', {
        message: 'Kalshi stream error',
        level: 'error',
      });
      emit();
    });

    marketStream.onOrderbookUpdate((u) => {
      lastUpdateMs = Date.now();

      const existing = marketBooks.get(u.ticker);
      if (existing) {
        existing.yes = u.yes.map((lvl) => ({ priceCents: lvl.price, size: lvl.quantity }));
        existing.no = u.no.map((lvl) => ({ priceCents: lvl.price, size: lvl.quantity }));
        existing.tsMs = lastUpdateMs;
      } else {
        marketBooks.set(u.ticker, {
          marketTicker: u.ticker,
          yes: u.yes.map((lvl) => ({ priceCents: lvl.price, size: lvl.quantity })),
          no: u.no.map((lvl) => ({ priceCents: lvl.price, size: lvl.quantity })),
          tsMs: lastUpdateMs,
        });
      }

      rebuildGames();
      emit();
    });

    // Fire connect (don't await — runs in parallel with REST fetches)
    const wsConnectPromise = marketStream.connect(accessKeyId, privateKey, environment);

    // Pre-compute event tickers so both branches have inputs ready
    const grouped = groupMarketsByEvent(discoveredMarkets);
    const eventTickers = Array.from(grouped.keys());
    // Also fetch metadata for scaffold events so they get start times + team names
    const scaffoldTickers = Array.from(scaffoldGroupedEvents.keys());
    const allEventTickers = [...eventTickers, ...scaffoldTickers];

    // Fetch orderbooks + events in parallel (combined progress counter)
    let fetchedItems = 0;
    const totalItems = tickers.length + allEventTickers.length;
    updateLoadingState('fetching-data', {
      message: 'Fetching market data',
      done: 0,
      total: totalItems,
    });
    emit();

    let obPopulated = 0;
    await Promise.all([
      // Branch 1: Orderbooks (progressive — populate marketBooks inline, rebuild every 5)
      mapConcurrent(tickers, 15, async (ticker) => {
        try {
          const ob = await api.getOrderbook(ticker);
          if (ob) {
            marketBooks.set(ticker, {
              marketTicker: ticker,
              yes: orderbookToLevels(ob.yes),
              no: orderbookToLevels(ob.no),
              tsMs: Date.now(),
            });
            obPopulated += 1;
            if (obPopulated % 5 === 0) {
              rebuildGames();
              lastUpdateMs = Date.now();
            }
          }
          fetchedItems += 1;
          updateLoadingState('fetching-data', { done: fetchedItems, total: totalItems });
          emit();
        } catch (err) {
          console.warn(`Failed to fetch orderbook for ${ticker}:`, err);
          fetchedItems += 1;
          updateLoadingState('fetching-data', {
            message: `Orderbook fetch failed: ${ticker}`,
            done: fetchedItems,
            total: totalItems,
            level: 'warn',
          });
          emit();
        }
      }),

      // Branch 2: Events (start times + team names)
      (async () => {
        const events = await mapConcurrent(allEventTickers, 15, async (eventTicker) => {
          try {
            const event = await api.getEvent(eventTicker);
            fetchedItems += 1;
            updateLoadingState('fetching-data', { done: fetchedItems, total: totalItems });
            emit();
            return { eventTicker, event };
          } catch (err) {
            console.warn(`Failed to fetch event ${eventTicker}:`, err);
            fetchedItems += 1;
            updateLoadingState('fetching-data', {
              message: `Event fetch failed: ${eventTicker}`,
              done: fetchedItems,
              total: totalItems,
              level: 'warn',
            });
            emit();
            return { eventTicker, event: null };
          }
        });

        for (const { eventTicker, event } of events) {
          const rawStartTime = event?.start_time ?? null;
          if (rawStartTime) setEventStartTimeUtc(eventTicker, rawStartTime);
          let startTimePt: string | null = null;

          if (rawStartTime) {
            try {
              const adjustedStartTime = applyStartTimeOffset(rawStartTime);
              const parts = new Intl.DateTimeFormat('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'America/Los_Angeles',
              }).formatToParts(new Date(adjustedStartTime));
              const hh = parts.find((p) => p.type === 'hour')?.value;
              const mm = parts.find((p) => p.type === 'minute')?.value;
              if (hh && mm) startTimePt = `${hh}:${mm}`;
            } catch {
              startTimePt = null;
            }
          }

          eventStartTimes.set(eventTicker, startTimePt);

          // Parse team names from event title (more reliable than ticker parsing for CBB)
          if (event?.title) {
            const parsedNames = parseTeamNamesFromTitle(event.title);
            if (parsedNames) {
              eventTeamNames.set(eventTicker, parsedNames);
            }
          }
        }
      })(),
    ]);

    // Build final game structures (merges orderbooks + event metadata)
    updateLoadingState('building-rows', { message: 'Building game rows' });
    emit();
    rebuildGames();

    lastUpdateMs = Date.now();
    emit();

    // Await WS connect (likely already resolved — was started before REST fetches)
    updateLoadingState('connecting-stream', { message: 'Connecting live stream' });
    emit();
    await wsConnectPromise;
    marketStream.subscribeOrderbook(tickers);
    updateLoadingState('live', { message: 'Live stream connected' });
    emit();

    // Hydrate Polymarket data in background (non-blocking)
    polyHydrationCancelled = false;
    const hydrationDeps: PolyHydrationDeps = {
      sport,
      marketType,
      useRelay,
      discoveredMarkets,
      gamesByEvent,
      polyTokenToEvent,
      polyMoneylineByEvent,
      polySpreadByEvent,
      polyTotalByEvent,
      emit,
      updateLoadingState,
      getLoadingPhase: () => loadingState.phase,
      setLastUpdateMs: (ms: number) => {
        lastUpdateMs = ms;
      },
    };

    void (async () => {
      try {
        const ps1 = await hydratePolyMoneylines(hydrationDeps);
        if (ps1) polyStream = ps1;
        if (polyHydrationCancelled) return;
        const ps2 = await hydratePolySpreads(hydrationDeps);
        if (ps2) polyStream = ps2;
        if (polyHydrationCancelled) return;
        const ps3 = await hydratePolyTotals(hydrationDeps);
        if (ps3) polyStream = ps3;
        if (polyHydrationCancelled) return;
        lastUpdateMs = Date.now();
        emit();
      } catch (err) {
        if (!polyHydrationCancelled) {
          console.warn('Polymarket hydration failed:', err);
          updateLoadingState('live', { message: 'Polymarket hydration failed', level: 'warn' });
          emit();
        }
      }
    })();
  };

  return {
    start: async () => {
      if (marketStream && marketStream.isConnected()) return;
      try {
        await initialize();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        updateLoadingState('error', { message, level: 'error' });
        emit();
        throw error;
      }
    },

    stop: () => {
      polyHydrationCancelled = true;
      if (marketStream) {
        marketStream.disconnect();
        marketStream = null;
      }
      if (polyStream) {
        polyStream.disconnect();
        polyStream = null;
      }
      gamesByEvent.clear();
      marketBooks.clear();
      eventStartTimes.clear();
      eventTeamNames.clear();
      clearEventStartTimes();
      discoveredMarkets = [];
      fallbackEventScaffoldOnly = false;
      scaffoldGroupedEvents.clear();
      tickers = [];
      lastUpdateMs = 0;
      polyTokenToEvent.clear();
      polyMoneylineByEvent.clear();
      polySpreadByEvent.clear();
      polyTotalByEvent.clear();
      loadingState = {
        isLoading: false,
        phase: 'idle',
        startedAtMs: 0,
        updatedAtMs: 0,
        events: [],
      };
    },

    onUpdate: (cb) => callbacks.add(cb),
    offUpdate: (cb) => callbacks.delete(cb),
  };
}

/** @forge-footer
 * @checklist
 *   1. STATE CLEANUP | stop() must clear all Maps + arrays (11 total) | memory leak if missed
 *   2. PARALLEL FETCH | orderbooks + events concurrent | shared progress counter
 *   3. POLY HYDRATION | background void IIFE | polyHydrationCancelled checked between phases
 *   4. WS LIFECYCLE | connect fires before REST | subscribeOrderbook after connect resolves
 *   5. MAX_PER_EVENT | filters exotic lines to 6 per event | reduces REST volume
 *   6. BUG-009 | scaffold events from moneyline cross-reference | spreads/totals only
 * @sync-group  stream-types: sportsStream/types.ts + sportsStream/stream.ts + sportsStream/gameBuilder.ts
 */
